import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CANVAS_DEFAULT_FONT_FAMILY } from "../../shared/font-presets";
import type {
  MeetBackgroundBarWidth,
  MeetBackgroundCorner,
  MeetBackgroundLayoutVariant,
} from "../meet-background.interface";
import {
  MEET_LAYOUT,
  buildMeetPreviewLayers,
  meetFallbackQrTarget,
} from "../meet-background.util";
import { OVERLAY_QR } from "../../shared/qr-render";

@Component({
  selector: "app-meet-background-preview",
  templateUrl: "./meet-background-preview.component.html",
  styleUrls: ["./meet-background-preview.component.scss"],
  standalone: false,
})
export class MeetBackgroundPreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private static readonly CANVAS_WIDTH = MEET_LAYOUT.frame.width;
  private static readonly CANVAS_HEIGHT = MEET_LAYOUT.frame.height;
  readonly fallbackQrData = meetFallbackQrTarget();
  readonly overlayQr = OVERLAY_QR;

  @ViewChild("previewStage", { static: true })
  private previewStageRef!: ElementRef<HTMLDivElement>;

  @Input() layoutVariant: MeetBackgroundLayoutVariant = "corner";
  @Input() barWidth: MeetBackgroundBarWidth = "compact";
  @Input() corner: MeetBackgroundCorner = "bottom-right";
  @Input() logoCorner: MeetBackgroundCorner = "top-left";
  @Input() showQrCaption = false;
  @Input() qrCaption: string | null = null;
  @Input() showEmployeeName = false;
  @Input() previewEmployeeName: string | null = null;
  @Input() showJobTitle = false;
  @Input() previewJobTitle: string | null = null;
  @Input() showLogo = false;
  @Input() logoUrl: string | null = null;
  @Input() qrData: string | null = null;
  @Input() opacity = 0.92;
  @Input() qrBackgroundColor = "#f5f5f5";
  @Input() logoOpacity = 0.92;
  @Input() logoBackgroundColor = "#ffffff";
  @Input() marginPx = 24;
  @Input() logoMarginPx = 24;
  @Input() sizeRatio = MEET_LAYOUT.scale.default;
  @Input() logoSizeRatio = MEET_LAYOUT.logoScale.default;
  @Input() fontFamily = CANVAS_DEFAULT_FONT_FAMILY;
  @Input() fontCssUrl: string | null = null;
  @Input() companyFontFileUrl: string | null = null;
  @Input() loading = false;
  @Input() renderedImageUrl: string | null = null;

  canvasScale = 1;
  private resizeObserver: ResizeObserver | null = null;
  private injectedFontNode: HTMLElement | null = null;

  get canvasTransform(): string {
    return `translate(-50%, -50%) scale(${this.canvasScale})`;
  }

  get textFontFamily(): string {
    return this.fontFamily?.trim() || CANVAS_DEFAULT_FONT_FAMILY;
  }

  get layers() {
    return buildMeetPreviewLayers({
      layoutVariant: this.layoutVariant,
      barWidth: this.barWidth,
      corner: this.corner,
      logoCorner: this.logoCorner,
      sizeRatio: this.sizeRatio,
      logoSizeRatio: this.logoSizeRatio,
      opacity: this.opacity,
      qrBackgroundColor: this.qrBackgroundColor,
      logoOpacity: this.logoOpacity,
      logoBackgroundColor: this.logoBackgroundColor,
      marginPx: this.marginPx,
      logoMarginPx: this.logoMarginPx,
      showQrCaption: this.showQrCaption,
      qrCaption: this.qrCaption,
      showLogo: this.showLogo,
      logoUrl: this.logoUrl,
      renderedImageUrl: this.renderedImageUrl,
    });
  }

  ngAfterViewInit(): void {
    this.recalculateScale();
    this.resizeObserver = new ResizeObserver(() => this.recalculateScale());
    this.resizeObserver.observe(this.previewStageRef.nativeElement);
    this.syncPreviewFontFace();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["fontFamily"] ||
      changes["fontCssUrl"] ||
      changes["companyFontFileUrl"]
    ) {
      this.syncPreviewFontFace();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.recalculateScale();
  }

  private syncPreviewFontFace(): void {
    const cssUrl = this.fontCssUrl?.trim() || "";
    const companyUrl = this.companyFontFileUrl?.trim() || "";
    const family = (this.fontFamily.split(",")[0] ?? "")
      .trim()
      .replace(/^["']+|["']+$/g, "");
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
    if (cssUrl) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssUrl;
      link.setAttribute("data-meet-preview-font", "1");
      document.head.appendChild(link);
      this.injectedFontNode = link;
      return;
    }
    if (companyUrl && family) {
      const style = document.createElement("style");
      style.setAttribute("data-meet-preview-font", "1");
      style.textContent = `@font-face{font-family:"${family.replace(/"/g, "")}";src:url("${companyUrl}");font-display:swap;}`;
      document.head.appendChild(style);
      this.injectedFontNode = style;
    }
  }

  private recalculateScale(): void {
    const stage = this.previewStageRef.nativeElement;
    const { width, height } = stage.getBoundingClientRect();
    const styles = window.getComputedStyle(stage);
    const paddingX =
      (parseFloat(styles.paddingLeft || "0") || 0) +
      (parseFloat(styles.paddingRight || "0") || 0);
    const paddingY =
      (parseFloat(styles.paddingTop || "0") || 0) +
      (parseFloat(styles.paddingBottom || "0") || 0);
    const innerWidth = Math.max(0, width - paddingX);
    const innerHeight = Math.max(0, height - paddingY);
    if (innerWidth <= 0 || innerHeight <= 0) {
      this.canvasScale = 1;
      return;
    }
    const safeWidth = Math.max(0, innerWidth - 2);
    const safeHeight = Math.max(0, innerHeight - 2);
    const scaleX = safeWidth / MeetBackgroundPreviewComponent.CANVAS_WIDTH;
    const scaleY = safeHeight / MeetBackgroundPreviewComponent.CANVAS_HEIGHT;
    this.canvasScale = Math.max(0.05, Math.min(scaleX, scaleY, 1));
  }
}
